import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Image as ImageIcon, Video, Mic, FileText, X, Smile, Users, Globe, ChevronUp, Eye, EyeOff, Trash2, BarChart3, ThumbsUp, MessageCircle, Share2, TrendingUp, Activity, Search, Filter, CreditCard as Edit2, RotateCcw, Check, AlertCircle, Camera } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';

interface Comment {
  id: string;
  post_id: string;
  consultant_id: string;
  content: string;
  created_at: string;
  consultant_profiles?: {
    first_name: string;
    last_name: string;
    photo_url: string | null;
    profile_photo_url: string | null;
  };
}

interface Post {
  id: string;
  consultant_id: string;
  content: string;
  post_type: string;
  media_urls: string[];
  media_type: string | null;
  hashtags: string[];
  visibility: 'community' | 'public';
  likes_count: number;
  comments_count: number;
  shares_count: number;
  is_hidden: boolean;
  is_admin_post?: boolean;
  created_at: string;
  consultant_profiles?: {
    first_name: string;
    last_name: string;
    email: string;
    photo_url: string | null;
    profile_photo_url: string | null;
  };
  userLiked?: boolean;
  comments?: Comment[];
  showComments?: boolean;
}

interface Consultant {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  post_count?: number;
}

interface Analytics {
  totalPosts: number;
  publicPosts: number;
  communityPosts: number;
  totalLikes: number;
  totalComments: number;
  totalShares: number;
  hiddenPosts: number;
  activeConsultants: number;
}

const COMMON_EMOJIS = ['👍', '❤️', '😊', '🎉', '🔥', '💯', '🚀', '💡', '✨', '🎯'];

export default function CommunityConnect() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [activeTab, setActiveTab] = useState<'post' | 'analytics' | 'manage'>('post');

  const [content, setContent] = useState('');
  const [postType, setPostType] = useState<'text' | 'image' | 'video' | 'podcast'>('text');
  const [mediaFiles, setMediaFiles] = useState<File[]>([]);
  const [mediaPreviews, setMediaPreviews] = useState<string[]>([]);
  const [visibility, setVisibility] = useState<'community' | 'public'>('community');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const [posts, setPosts] = useState<Post[]>([]);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [consultants, setConsultants] = useState<Consultant[]>([]);
  const [loadingConsultants, setLoadingConsultants] = useState(true);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);

  const [consultantSearch, setConsultantSearch] = useState('');
  const [postFilter, setPostFilter] = useState<'all' | 'community' | 'public' | 'hidden'>('all');

  const [editingPostId, setEditingPostId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [commentInputs, setCommentInputs] = useState<Record<string, string>>({});
  const [loadingComments, setLoadingComments] = useState<Record<string, boolean>>({});
  const [submittingComment, setSubmittingComment] = useState<Record<string, boolean>>({});
  const [toastMsg, setToastMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [adminPhotoUrl, setAdminPhotoUrl] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    fetchPosts();
    if (activeTab === 'analytics' || activeTab === 'manage') {
      fetchConsultants();
      computeAnalytics();
    }

    const channel = supabase
      .channel('admin_community_posts_realtime')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'consultant_community_posts' },
        () => {
          fetchPosts();
          if (activeTab === 'analytics' || activeTab === 'manage') {
            computeAnalytics();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [activeTab]);

  const showToast = (type: 'success' | 'error', text: string) => {
    setToastMsg({ type, text });
    setTimeout(() => setToastMsg(null), 3500);
  };

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate('/admin/login');
      return;
    }
    const { data } = await supabase
      .from('admin_profiles')
      .select('photo_url')
      .eq('id', session.user.id)
      .maybeSingle();
    if (data?.photo_url) setAdminPhotoUrl(data.photo_url);
  };

  const handleAdminPhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    setUploadingPhoto(true);
    try {
      const ext = file.name.split('.').pop();
      const path = `${user.id}/admin-profile.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from('profile-photos')
        .upload(path, file, { upsert: true });
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from('profile-photos').getPublicUrl(path);
      const { error: updateError } = await supabase
        .from('admin_profiles')
        .update({ photo_url: publicUrl })
        .eq('id', user.id);
      if (updateError) throw updateError;
      setAdminPhotoUrl(publicUrl);
      showToast('success', 'Profile photo updated.');
    } catch (err) {
      console.error('Error uploading photo:', err);
      showToast('error', 'Failed to upload photo.');
    } finally {
      setUploadingPhoto(false);
      if (photoInputRef.current) photoInputRef.current.value = '';
    }
  };

  const fetchPosts = async () => {
    setLoadingPosts(true);
    try {
      const { data: postsData, error } = await supabase
        .from('consultant_community_posts')
        .select('id, consultant_id, content, post_type, media_urls, media_type, hashtags, visibility, likes_count, comments_count, shares_count, is_hidden, is_admin_post, created_at')
        .order('created_at', { ascending: false });
      if (error) throw error;

      const posts = postsData || [];

      const consultantIds = [...new Set(
        posts.filter(p => !p.is_admin_post && p.consultant_id).map(p => p.consultant_id)
      )];

      let profileMap: Record<string, { first_name: string; last_name: string; email: string; photo_url: string | null; profile_photo_url: string | null }> = {};
      if (consultantIds.length > 0) {
        const { data: profiles } = await supabase
          .from('consultant_profiles')
          .select('id, first_name, last_name, email, photo_url, profile_photo_url')
          .in('id', consultantIds);
        (profiles || []).forEach(p => { profileMap[p.id] = p; });
      }

      let likedPostIds: Set<string> = new Set();
      if (user) {
        const { data: likes } = await supabase
          .from('consultant_post_likes')
          .select('post_id')
          .eq('consultant_id', user.id);
        likedPostIds = new Set((likes || []).map((l: { post_id: string }) => l.post_id));
      }

      setPosts(posts.map((p) => ({
        ...p,
        consultant_profiles: profileMap[p.consultant_id] || null,
        userLiked: likedPostIds.has(p.id),
        showComments: false,
        comments: [],
      })));
    } catch (err) {
      console.error('Error fetching posts:', err);
    } finally {
      setLoadingPosts(false);
    }
  };

  const fetchComments = async (postId: string) => {
    setLoadingComments(prev => ({ ...prev, [postId]: true }));
    try {
      const { data, error } = await supabase
        .from('consultant_post_comments')
        .select(`*, consultant_profiles(first_name, last_name, photo_url, profile_photo_url)`)
        .eq('post_id', postId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      setPosts(prev => prev.map(p =>
        p.id === postId ? { ...p, comments: data || [] } : p
      ));
    } catch (err) {
      console.error('Error fetching comments:', err);
    } finally {
      setLoadingComments(prev => ({ ...prev, [postId]: false }));
    }
  };

  const fetchConsultants = async () => {
    setLoadingConsultants(true);
    try {
      const { data, error } = await supabase
        .from('consultant_profiles')
        .select('id, first_name, last_name, email')
        .order('first_name', { ascending: true });
      if (error) throw error;

      const { data: postCounts } = await supabase
        .from('consultant_community_posts')
        .select('consultant_id');

      const counts: Record<string, number> = {};
      (postCounts || []).forEach((p: { consultant_id: string }) => {
        counts[p.consultant_id] = (counts[p.consultant_id] || 0) + 1;
      });

      setConsultants((data || []).map((c: Consultant) => ({ ...c, post_count: counts[c.id] || 0 })));
    } catch (err) {
      console.error('Error fetching consultants:', err);
    } finally {
      setLoadingConsultants(false);
    }
  };

  const computeAnalytics = async () => {
    try {
      const { data } = await supabase
        .from('consultant_community_posts')
        .select('visibility, likes_count, comments_count, shares_count, is_hidden, consultant_id');
      if (!data) return;

      setAnalytics({
        totalPosts: data.length,
        publicPosts: data.filter(p => p.visibility === 'public').length,
        communityPosts: data.filter(p => p.visibility === 'community').length,
        totalLikes: data.reduce((s, p) => s + (p.likes_count || 0), 0),
        totalComments: data.reduce((s, p) => s + (p.comments_count || 0), 0),
        totalShares: data.reduce((s, p) => s + (p.shares_count || 0), 0),
        hiddenPosts: data.filter(p => p.is_hidden).length,
        activeConsultants: new Set(data.map(p => p.consultant_id)).size,
      });
    } catch (err) {
      console.error('Error computing analytics:', err);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>, type: 'image' | 'video' | 'podcast') => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setPostType(type);
    setMediaFiles(files);
    setMediaPreviews(files.map(f => URL.createObjectURL(f)));
  };

  const removeMedia = (index: number) => {
    URL.revokeObjectURL(mediaPreviews[index]);
    setMediaFiles(prev => prev.filter((_, i) => i !== index));
    setMediaPreviews(prev => prev.filter((_, i) => i !== index));
    if (mediaFiles.length <= 1) setPostType('text');
  };

  const uploadMedia = async (): Promise<string[]> => {
    const urls: string[] = [];
    for (const file of mediaFiles) {
      const ext = file.name.split('.').pop();
      const path = `admin/${Date.now()}-${Math.random()}.${ext}`;
      const { error } = await supabase.storage.from('community-media').upload(path, file);
      if (error) continue;
      const { data: { publicUrl } } = supabase.storage.from('community-media').getPublicUrl(path);
      urls.push(publicUrl);
    }
    return urls;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim() && !mediaFiles.length) return;
    if (!user) return;

    setIsSubmitting(true);
    try {
      let mediaUrls: string[] = [];
      let mediaType: string | null = null;

      if (mediaFiles.length) {
        mediaUrls = await uploadMedia();
        if (postType === 'image') mediaType = 'image';
        else if (postType === 'video') mediaType = 'video';
        else if (postType === 'podcast') mediaType = 'audio';
      }

      const hashtags = content.match(/#\w+/g)?.map(t => t.substring(1)) || [];

      const { error } = await supabase
        .from('consultant_community_posts')
        .insert({
          consultant_id: user.id,
          content: content.trim(),
          post_type: postType,
          media_urls: mediaUrls,
          media_type: mediaType,
          hashtags,
          visibility,
          is_admin_post: true,
          admin_photo_url: adminPhotoUrl || null
        });

      if (error) throw error;

      setContent('');
      setMediaFiles([]);
      setMediaPreviews([]);
      setPostType('text');
      setVisibility('community');
      setIsExpanded(false);
      setShowEmojiPicker(false);
      fetchPosts();
      showToast('success', 'Post published successfully.');
    } catch (err) {
      console.error('Error creating post:', err);
      showToast('error', 'Failed to create post. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeletePost = async (postId: string) => {
    if (!confirm('Are you sure you want to delete this post? This cannot be undone.')) return;
    try {
      const { error } = await supabase.from('consultant_community_posts').delete().eq('id', postId);
      if (error) throw error;
      setPosts(prev => prev.filter(p => p.id !== postId));
      computeAnalytics();
      showToast('success', 'Post deleted.');
    } catch (err) {
      console.error('Error deleting post:', err);
      showToast('error', 'Failed to delete post.');
    }
  };

  const handleToggleHide = async (post: Post) => {
    try {
      const { error } = await supabase
        .from('consultant_community_posts')
        .update({ is_hidden: !post.is_hidden })
        .eq('id', post.id);
      if (error) throw error;
      setPosts(prev => prev.map(p => p.id === post.id ? { ...p, is_hidden: !p.is_hidden } : p));
      computeAnalytics();
      showToast('success', post.is_hidden ? 'Post restored.' : 'Post hidden.');
    } catch (err) {
      console.error('Error toggling post visibility:', err);
      showToast('error', 'Failed to update post.');
    }
  };

  const handleEditPost = async (postId: string) => {
    if (!editContent.trim()) return;
    try {
      const { error } = await supabase
        .from('consultant_community_posts')
        .update({ content: editContent.trim(), updated_at: new Date().toISOString() })
        .eq('id', postId);
      if (error) throw error;
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, content: editContent.trim() } : p));
      setEditingPostId(null);
      setEditContent('');
      showToast('success', 'Post updated.');
    } catch (err) {
      console.error('Error editing post:', err);
      showToast('error', 'Failed to update post.');
    }
  };

  const handleLike = async (post: Post) => {
    if (!user) return;
    try {
      if (post.userLiked) {
        const { error } = await supabase
          .from('consultant_post_likes')
          .delete()
          .eq('post_id', post.id)
          .eq('consultant_id', user.id);
        if (error) throw error;
        await supabase
          .from('consultant_community_posts')
          .update({ likes_count: Math.max(0, (post.likes_count || 0) - 1) })
          .eq('id', post.id);
        setPosts(prev => prev.map(p =>
          p.id === post.id ? { ...p, userLiked: false, likes_count: Math.max(0, (p.likes_count || 0) - 1) } : p
        ));
      } else {
        const { error } = await supabase
          .from('consultant_post_likes')
          .insert({ post_id: post.id, consultant_id: user.id });
        if (error) throw error;
        await supabase
          .from('consultant_community_posts')
          .update({ likes_count: (post.likes_count || 0) + 1 })
          .eq('id', post.id);
        setPosts(prev => prev.map(p =>
          p.id === post.id ? { ...p, userLiked: true, likes_count: (p.likes_count || 0) + 1 } : p
        ));
      }
    } catch (err) {
      console.error('Error toggling like:', err);
    }
  };

  const handleToggleComments = (post: Post) => {
    if (!post.showComments && (!post.comments || post.comments.length === 0)) {
      fetchComments(post.id);
    }
    setPosts(prev => prev.map(p => p.id === post.id ? { ...p, showComments: !p.showComments } : p));
  };

  const handleAddComment = async (post: Post) => {
    const text = (commentInputs[post.id] || '').trim();
    if (!text || !user) return;
    setSubmittingComment(prev => ({ ...prev, [post.id]: true }));
    try {
      const { data, error } = await supabase
        .from('consultant_post_comments')
        .insert({ post_id: post.id, consultant_id: user.id, content: text })
        .select(`*, consultant_profiles(first_name, last_name, photo_url, profile_photo_url)`)
        .single();
      if (error) throw error;
      await supabase
        .from('consultant_community_posts')
        .update({ comments_count: (post.comments_count || 0) + 1 })
        .eq('id', post.id);
      setPosts(prev => prev.map(p =>
        p.id === post.id
          ? { ...p, comments: [...(p.comments || []), data], comments_count: (p.comments_count || 0) + 1 }
          : p
      ));
      setCommentInputs(prev => ({ ...prev, [post.id]: '' }));
    } catch (err) {
      console.error('Error adding comment:', err);
      showToast('error', 'Failed to post comment.');
    } finally {
      setSubmittingComment(prev => ({ ...prev, [post.id]: false }));
    }
  };

  const handleDeleteComment = async (post: Post, commentId: string) => {
    try {
      const { error } = await supabase
        .from('consultant_post_comments')
        .delete()
        .eq('id', commentId);
      if (error) throw error;
      await supabase
        .from('consultant_community_posts')
        .update({ comments_count: Math.max(0, (post.comments_count || 0) - 1) })
        .eq('id', post.id);
      setPosts(prev => prev.map(p =>
        p.id === post.id
          ? { ...p, comments: (p.comments || []).filter(c => c.id !== commentId), comments_count: Math.max(0, (p.comments_count || 0) - 1) }
          : p
      ));
    } catch (err) {
      console.error('Error deleting comment:', err);
      showToast('error', 'Failed to delete comment.');
    }
  };

  const filteredPosts = posts.filter(p => {
    if (postFilter === 'hidden') return p.is_hidden;
    if (postFilter === 'public') return p.visibility === 'public' && !p.is_hidden;
    if (postFilter === 'community') return p.visibility === 'community' && !p.is_hidden;
    return true;
  });

  const filteredConsultants = consultants.filter(c =>
    `${c.first_name} ${c.last_name} ${c.email}`.toLowerCase().includes(consultantSearch.toLowerCase())
  );

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffH = (now.getTime() - date.getTime()) / 3600000;
    if (diffH < 1) return `${Math.floor(diffH * 60)}m ago`;
    if (diffH < 24) return `${Math.floor(diffH)}h ago`;
    if (diffH < 168) return `${Math.floor(diffH / 24)}d ago`;
    return date.toLocaleDateString();
  };

  const getAvatar = (profile: { first_name?: string; last_name?: string; photo_url?: string | null; profile_photo_url?: string | null } | undefined, fallback: string) => {
    const photoUrl = profile?.photo_url || profile?.profile_photo_url;
    if (photoUrl) return { type: 'img' as const, src: photoUrl };
    const name = profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() : fallback;
    return { type: 'initials' as const, text: name.charAt(0) || 'P' };
  };

  const statCards = analytics ? [
    { label: 'Total Posts', value: analytics.totalPosts, icon: FileText, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-100' },
    { label: 'Public Posts', value: analytics.publicPosts, icon: Globe, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-100' },
    { label: 'Community Posts', value: analytics.communityPosts, icon: Users, color: 'text-[#2ab5a5]', bg: 'bg-[#40E0D0]/10', border: 'border-[#40E0D0]/20' },
    { label: 'Total Likes', value: analytics.totalLikes, icon: ThumbsUp, color: 'text-pink-600', bg: 'bg-pink-50', border: 'border-pink-100' },
    { label: 'Total Comments', value: analytics.totalComments, icon: MessageCircle, color: 'text-yellow-600', bg: 'bg-yellow-50', border: 'border-yellow-100' },
    { label: 'Total Shares', value: analytics.totalShares, icon: Share2, color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-100' },
    { label: 'Hidden Posts', value: analytics.hiddenPosts, icon: EyeOff, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-100' },
    { label: 'Active Posters', value: analytics.activeConsultants, icon: Activity, color: 'text-teal-600', bg: 'bg-teal-50', border: 'border-teal-100' },
  ] : [];

  const PostCard = ({ post }: { post: Post }) => {
    const isAdminPost = post.is_admin_post || !post.consultant_profiles;
    const profile = isAdminPost ? undefined : post.consultant_profiles;
    const name = isAdminPost
      ? 'Perplexity Team'
      : `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() || 'Unknown';
    const avatar = isAdminPost
      ? (adminPhotoUrl
          ? { type: 'img' as const, src: adminPhotoUrl }
          : { type: 'initials' as const, text: 'PT' })
      : getAvatar(profile, name);
    const isEditing = editingPostId === post.id;
    const mediaUrls: string[] = Array.isArray(post.media_urls) ? post.media_urls : [];

    return (
      <div className={`bg-white rounded-xl border shadow-sm transition-all ${post.is_hidden ? 'border-red-200 bg-red-50/30' : 'border-gray-200'}`}>
        {post.is_hidden && (
          <div className="flex items-center gap-2 px-5 pt-4 pb-0 text-red-600 text-xs font-medium">
            <EyeOff className="w-3.5 h-3.5" />
            This post is hidden from the community
          </div>
        )}

        <div className="p-5">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-start space-x-3">
              {avatar.type === 'img' ? (
                <img src={avatar.src} alt={name} className="w-10 h-10 rounded-full object-cover flex-shrink-0 border border-gray-200" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-[#40E0D0]/15 flex items-center justify-center text-[#2ab5a5] font-bold text-sm flex-shrink-0">
                  {avatar.text}
                </div>
              )}
              <div>
                <p className="text-gray-900 font-semibold text-sm">{name}</p>
                <p className="text-gray-400 text-xs">
                  {formatDate(post.created_at)} ·{' '}
                  <span className={post.visibility === 'public' ? 'text-green-600' : 'text-[#2ab5a5]'}>
                    {post.visibility === 'public' ? 'Public' : 'Community'}
                  </span>
                </p>
              </div>
            </div>

            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={() => {
                  if (isEditing) {
                    setEditingPostId(null);
                    setEditContent('');
                  } else {
                    setEditingPostId(post.id);
                    setEditContent(post.content);
                  }
                }}
                title="Edit post"
                className={`p-1.5 rounded-lg transition-colors ${isEditing ? 'bg-[#40E0D0]/10 text-[#2ab5a5]' : 'hover:bg-gray-100 text-gray-400 hover:text-gray-700'}`}
              >
                <Edit2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleToggleHide(post)}
                title={post.is_hidden ? 'Restore post' : 'Hide post'}
                className={`p-1.5 rounded-lg transition-colors ${post.is_hidden ? 'bg-green-50 text-green-600 hover:bg-green-100' : 'hover:bg-gray-100 text-gray-400 hover:text-yellow-600'}`}
              >
                {post.is_hidden ? <RotateCcw className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </button>
              <button
                onClick={() => handleDeletePost(post.id)}
                title="Delete post"
                className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>

          {isEditing ? (
            <div className="mb-3">
              <textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                className="w-full px-3 py-2.5 border-2 border-[#40E0D0] rounded-lg text-gray-900 text-sm bg-white focus:outline-none resize-none"
                rows={4}
              />
              <div className="flex items-center gap-2 mt-2">
                <button
                  onClick={() => handleEditPost(post.id)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[#40E0D0] text-white text-xs font-semibold rounded-lg hover:bg-[#35c5b5] transition-colors"
                >
                  <Check className="w-3.5 h-3.5" />
                  Save
                </button>
                <button
                  onClick={() => { setEditingPostId(null); setEditContent(''); }}
                  className="px-3 py-1.5 text-gray-500 text-xs hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <p className="text-gray-800 text-sm whitespace-pre-wrap mb-3 leading-relaxed">{post.content}</p>
          )}

          {mediaUrls.length > 0 && (
            <div className={`mb-3 grid gap-2 rounded-lg overflow-hidden ${mediaUrls.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
              {mediaUrls.map((url, i) => (
                post.media_type === 'image' ? (
                  <img key={i} src={url} alt="" className="w-full object-cover rounded-lg max-h-80 border border-gray-100" />
                ) : post.media_type === 'video' ? (
                  <video key={i} src={url} controls className="w-full rounded-lg max-h-80" />
                ) : (
                  <audio key={i} src={url} controls className="w-full" />
                )
              ))}
            </div>
          )}

          {post.hashtags?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {post.hashtags.map((tag, i) => (
                <span key={i} className="text-xs text-[#2ab5a5] hover:underline cursor-pointer">#{tag}</span>
              ))}
            </div>
          )}

          <div className="flex items-center gap-1 pt-3 border-t border-gray-100">
            <button
              onClick={() => handleLike(post)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all flex-1 justify-center ${
                post.userLiked
                  ? 'bg-[#40E0D0]/10 text-[#2ab5a5]'
                  : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800'
              }`}
            >
              <ThumbsUp className={`w-4 h-4 ${post.userLiked ? 'fill-[#40E0D0]' : ''}`} />
              <span>{post.likes_count || 0}</span>
              <span className="text-xs">Like</span>
            </button>

            <button
              onClick={() => handleToggleComments(post)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors flex-1 justify-center"
            >
              <MessageCircle className="w-4 h-4" />
              <span>{post.comments_count || 0}</span>
              <span className="text-xs">Comment</span>
            </button>

            <div className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-400 flex-1 justify-center">
              <Share2 className="w-4 h-4" />
              <span>{post.shares_count || 0}</span>
              <span className="text-xs">Shares</span>
            </div>
          </div>
        </div>

        {post.showComments && (
          <div className="border-t border-gray-100 bg-gray-50/60 px-5 py-4 rounded-b-xl">
            {loadingComments[post.id] ? (
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-[#40E0D0]" />
              </div>
            ) : (
              <div className="space-y-3 mb-4">
                {(post.comments || []).length === 0 ? (
                  <p className="text-gray-400 text-xs text-center py-2">No comments yet. Be the first!</p>
                ) : (
                  (post.comments || []).map(comment => {
                    const cProfile = comment.consultant_profiles;
                    const cName = cProfile
                      ? `${cProfile.first_name || ''} ${cProfile.last_name || ''}`.trim()
                      : 'Admin';
                    const cAvatar = getAvatar(cProfile, cName);
                    return (
                      <div key={comment.id} className="flex items-start gap-2.5 group">
                        {cAvatar.type === 'img' ? (
                          <img src={cAvatar.src} alt={cName} className="w-8 h-8 rounded-full object-cover flex-shrink-0 border border-gray-200" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-[#40E0D0]/15 flex items-center justify-center text-[#2ab5a5] font-bold text-xs flex-shrink-0">
                            {cAvatar.text}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="bg-white rounded-xl px-3 py-2 border border-gray-100 shadow-sm">
                            <p className="text-xs font-semibold text-gray-900 mb-0.5">{cName}</p>
                            <p className="text-sm text-gray-700">{comment.content}</p>
                          </div>
                          <p className="text-xs text-gray-400 mt-1 ml-1">{formatDate(comment.created_at)}</p>
                        </div>
                        <button
                          onClick={() => handleDeleteComment(post, comment.id)}
                          className="p-1 opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-all rounded flex-shrink-0"
                          title="Delete comment"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            )}

            <div className="flex items-start gap-2.5">
              {adminPhotoUrl ? (
                <img src={adminPhotoUrl} alt="Perplexity Team" className="w-8 h-8 rounded-full object-cover flex-shrink-0 border border-gray-200" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-[#40E0D0] flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                  PT
                </div>
              )}
              <div className="flex-1 flex items-center gap-2">
                <input
                  type="text"
                  value={commentInputs[post.id] || ''}
                  onChange={(e) => setCommentInputs(prev => ({ ...prev, [post.id]: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddComment(post); } }}
                  placeholder="Write a comment as Perplexity Team..."
                  className="flex-1 px-3 py-2 rounded-xl border border-gray-200 bg-white text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent"
                />
                <button
                  onClick={() => handleAddComment(post)}
                  disabled={!commentInputs[post.id]?.trim() || submittingComment[post.id]}
                  className="p-2 bg-[#40E0D0] text-white rounded-lg hover:bg-[#35c5b5] disabled:bg-gray-200 disabled:text-gray-400 transition-colors flex-shrink-0"
                >
                  {submittingComment[post.id] ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {toastMsg && (
          <div className={`fixed top-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-xl shadow-lg border text-sm font-medium transition-all ${
            toastMsg.type === 'success'
              ? 'bg-white border-green-200 text-green-800'
              : 'bg-white border-red-200 text-red-700'
          }`}>
            {toastMsg.type === 'success'
              ? <Check className="w-4 h-4 text-green-500" />
              : <AlertCircle className="w-4 h-4 text-red-500" />
            }
            {toastMsg.text}
          </div>
        )}

        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Community Connect</h1>
          <p className="text-gray-500 mt-1">Post to the Workday Community as Perplexity Team, manage consultant posts, and view analytics.</p>
        </div>

        <div className="flex space-x-1 mb-6 bg-gray-100 rounded-lg p-1 w-fit">
          {[
            { id: 'post', label: 'Create Post' },
            { id: 'analytics', label: 'Analytics' },
            { id: 'manage', label: 'Manage Posts' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={`px-5 py-2 rounded-md text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'post' && (
          <div className="max-w-2xl">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
              <form onSubmit={handleSubmit}>
                {!isExpanded ? (
                  <div
                    onClick={() => setIsExpanded(true)}
                    className="p-5 cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center space-x-3 mb-4">
                      {adminPhotoUrl ? (
                        <img src={adminPhotoUrl} alt="Perplexity Team" className="w-10 h-10 rounded-full object-cover flex-shrink-0 border border-gray-200" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-[#40E0D0] flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                          PT
                        </div>
                      )}
                      <div>
                        <p className="text-gray-900 font-semibold text-sm">Perplexity Team</p>
                        <p className="text-gray-500 text-xs">Company</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div className="flex items-center space-x-1">
                        <div className="flex items-center space-x-1.5 px-3 py-2 rounded-lg text-gray-600">
                          <ImageIcon className="w-4 h-4 text-green-500" />
                          <span className="text-sm font-medium">Photo</span>
                        </div>
                        <div className="flex items-center space-x-1.5 px-3 py-2 rounded-lg text-gray-600">
                          <Video className="w-4 h-4 text-blue-500" />
                          <span className="text-sm font-medium">Video</span>
                        </div>
                        <div className="flex items-center space-x-1.5 px-3 py-2 rounded-lg text-gray-600">
                          <Mic className="w-4 h-4 text-orange-500" />
                          <span className="text-sm font-medium">Podcast</span>
                        </div>
                      </div>
                      <button type="button" className="flex items-center space-x-2 px-5 py-2 bg-[#40E0D0] hover:bg-[#35c5b5] text-white font-semibold rounded-lg transition-colors text-sm">
                        <Send className="w-4 h-4" />
                        <span>Post</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="relative group flex-shrink-0">
                          {adminPhotoUrl ? (
                            <img src={adminPhotoUrl} alt="Perplexity Team" className="w-10 h-10 rounded-full object-cover border border-gray-200" />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-[#40E0D0] flex items-center justify-center text-white font-bold text-sm">
                              PT
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); photoInputRef.current?.click(); }}
                            className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                            title="Change profile photo"
                          >
                            {uploadingPhoto ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-white" />
                            ) : (
                              <Camera className="w-4 h-4 text-white" />
                            )}
                          </button>
                          <input
                            ref={photoInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleAdminPhotoUpload}
                            className="hidden"
                          />
                        </div>
                        <div>
                          <p className="text-gray-900 font-semibold">Perplexity Team</p>
                          <p className="text-gray-500 text-sm">
                            Company ·{' '}
                            <button
                              type="button"
                              onClick={() => photoInputRef.current?.click()}
                              className="text-[#2ab5a5] hover:underline text-xs"
                            >
                              {adminPhotoUrl ? 'Change photo' : 'Add profile photo'}
                            </button>
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setIsExpanded(false)}
                        className="p-2 rounded-full hover:bg-gray-100 text-gray-400 transition-colors"
                      >
                        <ChevronUp className="w-5 h-5" />
                      </button>
                    </div>

                    <textarea
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      placeholder="Share Workday insights, announcements, or updates with the community..."
                      className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 bg-gray-50 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent transition-all resize-none mb-4"
                      rows={4}
                    />

                    {mediaPreviews.length > 0 && (
                      <div className="mb-4 grid grid-cols-2 gap-3">
                        {mediaPreviews.map((preview, index) => (
                          <div key={index} className="relative group">
                            {postType === 'image' ? (
                              <img src={preview} alt="" className="w-full h-40 object-cover rounded-lg border border-gray-200" />
                            ) : postType === 'video' ? (
                              <video src={preview} className="w-full h-40 object-cover rounded-lg" controls />
                            ) : (
                              <div className="w-full h-40 rounded-lg flex items-center justify-center bg-gray-100 border border-gray-200">
                                <FileText className="w-12 h-12 text-gray-400" />
                              </div>
                            )}
                            <button
                              type="button"
                              onClick={() => removeMedia(index)}
                              className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="mb-4 p-3 rounded-lg border border-gray-200 bg-gray-50">
                      <p className="text-sm font-medium mb-2 text-gray-700">Post Visibility</p>
                      <div className="flex items-center space-x-3">
                        {[
                          { val: 'community', label: 'Community Only', icon: Users },
                          { val: 'public', label: 'Public', icon: Globe },
                        ].map(({ val, label, icon: Icon }) => (
                          <button
                            key={val}
                            type="button"
                            onClick={() => setVisibility(val as typeof visibility)}
                            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all text-sm font-medium ${
                              visibility === val
                                ? 'bg-[#40E0D0] text-white'
                                : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-100'
                            }`}
                          >
                            <Icon className="w-4 h-4" />
                            <span>{label}</span>
                          </button>
                        ))}
                      </div>
                      <p className="text-xs mt-2 text-gray-500">
                        {visibility === 'community'
                          ? 'Only signed-up consultants can see this post'
                          : 'Everyone, including non-members, can see this post'}
                      </p>
                    </div>

                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div className="flex items-center space-x-1">
                        <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={(e) => handleFileSelect(e, 'image')} className="hidden" id="admin-img" />
                        <label htmlFor="admin-img" className="flex items-center space-x-1.5 px-3 py-2 rounded-lg cursor-pointer hover:bg-gray-100 text-gray-600 transition-colors text-sm">
                          <ImageIcon className="w-4 h-4 text-green-500" />
                          <span className="font-medium">Photo</span>
                        </label>

                        <input type="file" accept="video/*" onChange={(e) => handleFileSelect(e, 'video')} className="hidden" id="admin-video" />
                        <label htmlFor="admin-video" className="flex items-center space-x-1.5 px-3 py-2 rounded-lg cursor-pointer hover:bg-gray-100 text-gray-600 transition-colors text-sm">
                          <Video className="w-4 h-4 text-blue-500" />
                          <span className="font-medium">Video</span>
                        </label>

                        <input type="file" accept="audio/*" onChange={(e) => handleFileSelect(e, 'podcast')} className="hidden" id="admin-audio" />
                        <label htmlFor="admin-audio" className="flex items-center space-x-1.5 px-3 py-2 rounded-lg cursor-pointer hover:bg-gray-100 text-gray-600 transition-colors text-sm">
                          <Mic className="w-4 h-4 text-orange-500" />
                          <span className="font-medium">Podcast</span>
                        </label>

                        <button
                          type="button"
                          onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                          className="flex items-center space-x-1.5 px-3 py-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors"
                        >
                          <Smile className="w-4 h-4 text-yellow-500" />
                        </button>
                      </div>

                      <button
                        type="submit"
                        disabled={isSubmitting || (!content.trim() && !mediaFiles.length)}
                        className="flex items-center space-x-2 px-5 py-2 bg-[#40E0D0] hover:bg-[#35c5b5] disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors text-sm"
                      >
                        {isSubmitting ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white" />
                            <span>Posting...</span>
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4" />
                            <span>Post as Perplexity Team</span>
                          </>
                        )}
                      </button>
                    </div>

                    {showEmojiPicker && (
                      <div className="mt-3 p-3 rounded-lg bg-gray-50 border border-gray-200">
                        <div className="flex flex-wrap gap-2">
                          {COMMON_EMOJIS.map((emoji, i) => (
                            <button
                              key={i}
                              type="button"
                              onClick={() => { setContent(c => c + emoji); setShowEmojiPicker(false); }}
                              className="text-2xl hover:scale-125 transition-transform"
                            >
                              {emoji}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </form>
            </div>

            {loadingPosts ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#40E0D0]" />
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">Recent Community Posts</h2>
                  <span className="text-sm text-gray-400">{posts.length} total</span>
                </div>
                {posts.length === 0 ? (
                  <div className="text-center py-10 bg-white rounded-xl border border-gray-200 text-gray-500">
                    No posts yet. Create the first post above.
                  </div>
                ) : (
                  posts.map(post => <PostCard key={post.id} post={post} />)
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {statCards.map((card) => {
                const Icon = card.icon;
                return (
                  <div key={card.label} className={`bg-white rounded-xl p-5 border ${card.border} shadow-sm`}>
                    <div className={`w-10 h-10 rounded-lg ${card.bg} flex items-center justify-center mb-3`}>
                      <Icon className={`w-5 h-5 ${card.color}`} />
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{card.value}</p>
                    <p className="text-gray-500 text-sm mt-1">{card.label}</p>
                  </div>
                );
              })}
            </div>

            {analytics && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                  <h3 className="text-gray-900 font-semibold mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-[#40E0D0]" />
                    Post Visibility Breakdown
                  </h3>
                  <div className="space-y-4">
                    {[
                      { label: 'Public', value: analytics.publicPosts, color: 'bg-green-400' },
                      { label: 'Community Only', value: analytics.communityPosts, color: 'bg-[#40E0D0]' },
                      { label: 'Hidden', value: analytics.hiddenPosts, color: 'bg-red-400' },
                    ].map(({ label, value, color }) => (
                      <div key={label}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-600">{label}</span>
                          <span className="text-gray-900 font-medium">{value}</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2">
                          <div
                            className={`${color} h-2 rounded-full transition-all`}
                            style={{ width: analytics.totalPosts ? `${(value / analytics.totalPosts) * 100}%` : '0%' }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                  <h3 className="text-gray-900 font-semibold mb-4 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-[#40E0D0]" />
                    Engagement Overview
                  </h3>
                  <div className="space-y-4">
                    {[
                      { label: 'Likes', value: analytics.totalLikes, color: 'bg-pink-400' },
                      { label: 'Comments', value: analytics.totalComments, color: 'bg-yellow-400' },
                      { label: 'Shares', value: analytics.totalShares, color: 'bg-orange-400' },
                    ].map(({ label, value, color }) => {
                      const max = Math.max(analytics.totalLikes, analytics.totalComments, analytics.totalShares, 1);
                      return (
                        <div key={label}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-600">{label}</span>
                            <span className="text-gray-900 font-medium">{value}</span>
                          </div>
                          <div className="w-full bg-gray-100 rounded-full h-2">
                            <div className={`${color} h-2 rounded-full transition-all`} style={{ width: `${(value / max) * 100}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
              <h3 className="text-gray-900 font-semibold mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-[#40E0D0]" />
                Top Contributing Consultants
              </h3>
              {loadingConsultants ? (
                <div className="flex justify-center py-6">
                  <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-[#40E0D0]" />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-gray-500 border-b border-gray-200">
                        <th className="text-left pb-3 font-medium">Consultant</th>
                        <th className="text-left pb-3 font-medium">Email</th>
                        <th className="text-right pb-3 font-medium">Posts</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {[...consultants].sort((a, b) => (b.post_count || 0) - (a.post_count || 0)).slice(0, 10).map(c => (
                        <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                          <td className="py-3 text-gray-900">{c.first_name} {c.last_name}</td>
                          <td className="py-3 text-gray-500">{c.email}</td>
                          <td className="py-3 text-right">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              (c.post_count || 0) > 0 ? 'bg-[#40E0D0]/15 text-[#2ab5a5]' : 'bg-gray-100 text-gray-400'
                            }`}>
                              {c.post_count || 0}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'manage' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search consultants..."
                  value={consultantSearch}
                  onChange={(e) => setConsultantSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent text-sm"
                />
              </div>
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-400" />
                <select
                  value={postFilter}
                  onChange={(e) => setPostFilter(e.target.value as typeof postFilter)}
                  className="bg-white border border-gray-300 rounded-lg text-gray-900 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent"
                >
                  <option value="all">All Posts</option>
                  <option value="public">Public Only</option>
                  <option value="community">Community Only</option>
                  <option value="hidden">Hidden Posts</option>
                </select>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h3 className="text-gray-900 font-semibold">All Workday Consultants</h3>
                <p className="text-gray-500 text-sm mt-0.5">{filteredConsultants.length} consultants</p>
              </div>
              {loadingConsultants ? (
                <div className="flex justify-center py-10">
                  <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-[#40E0D0]" />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-gray-500 bg-white border-b border-gray-200">
                        <th className="text-left px-6 py-3 font-medium">Name</th>
                        <th className="text-left px-6 py-3 font-medium">Email</th>
                        <th className="text-right px-6 py-3 font-medium">Posts</th>
                        <th className="text-right px-6 py-3 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredConsultants.map(c => {
                        const consultantPosts = filteredPosts.filter(p => p.consultant_id === c.id);
                        return (
                          <React.Fragment key={c.id}>
                            <tr className="hover:bg-gray-50 transition-colors">
                              <td className="px-6 py-4">
                                <div className="flex items-center space-x-3">
                                  <div className="w-8 h-8 rounded-full bg-[#40E0D0]/15 flex items-center justify-center text-[#2ab5a5] font-bold text-sm flex-shrink-0">
                                    {c.first_name?.charAt(0) || '?'}
                                  </div>
                                  <span className="text-gray-900 font-medium">{c.first_name} {c.last_name}</span>
                                </div>
                              </td>
                              <td className="px-6 py-4 text-gray-500">{c.email}</td>
                              <td className="px-6 py-4 text-right">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                  (c.post_count || 0) > 0 ? 'bg-[#40E0D0]/15 text-[#2ab5a5]' : 'bg-gray-100 text-gray-400'
                                }`}>
                                  {c.post_count || 0} posts
                                </span>
                              </td>
                              <td className="px-6 py-4 text-right text-gray-400 text-xs">
                                {(c.post_count || 0) === 0 ? 'No posts' : ''}
                              </td>
                            </tr>
                            {consultantPosts.map(post => (
                              <tr key={post.id} className="bg-gray-50/60 hover:bg-gray-100/60 transition-colors">
                                <td className="px-6 py-3 pl-16" colSpan={2}>
                                  <p className="text-gray-700 text-xs line-clamp-2">{post.content}</p>
                                  <div className="flex items-center gap-3 mt-1 text-xs">
                                    <span className="text-gray-400">{formatDate(post.created_at)}</span>
                                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                      post.visibility === 'public'
                                        ? 'bg-green-100 text-green-700'
                                        : 'bg-[#40E0D0]/10 text-[#2ab5a5]'
                                    }`}>
                                      {post.visibility}
                                    </span>
                                    {post.is_hidden && (
                                      <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-600">Hidden</span>
                                    )}
                                    <span className="text-gray-400">{post.likes_count || 0} likes · {post.comments_count || 0} comments</span>
                                  </div>
                                </td>
                                <td className="px-6 py-3 text-right" />
                                <td className="px-6 py-3 text-right">
                                  <div className="flex items-center justify-end gap-1">
                                    <button
                                      onClick={() => handleToggleHide(post)}
                                      title={post.is_hidden ? 'Restore post' : 'Hide post'}
                                      className={`p-1.5 rounded-lg transition-colors ${post.is_hidden ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:text-yellow-600 hover:bg-yellow-50'}`}
                                    >
                                      {post.is_hidden ? <RotateCcw className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                                    </button>
                                    <button
                                      onClick={() => handleDeletePost(post.id)}
                                      title="Delete post"
                                      className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                  {filteredConsultants.length === 0 && (
                    <div className="text-center py-10 text-gray-500 text-sm">No consultants found.</div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
